import { FeedbackForm } from '../types/feedback';

export const feedbackForms: { [key: string]: FeedbackForm } = {
  general: {
    id: 'general',
    title: 'Share Your Feedback',
    description: 'Help us improve Kingdom Studio by sharing your thoughts and experiences.',
    feedbackType: 'general',
    submitText: 'Submit Feedback',
    questions: [
      {
        id: 'feedback_type',
        type: 'radio',
        question: 'What type of feedback are you sharing?',
        required: true,
        options: ['Bug Report', 'Feature Request', 'General Feedback', 'Compliment', 'Suggestion']
      },
      {
        id: 'satisfaction',
        type: 'rating',
        question: 'How satisfied are you with Kingdom Studio overall?',
        required: true,
        validation: { min: 1, max: 5 }
      },
      {
        id: 'features_used',
        type: 'checkbox',
        question: 'Which features do you use most? (Select all that apply)',
        options: [
          'Video Captions',
          'Thumbnail Generation', 
          'Audio Generation',
          'Prompt Generator',
          'Script Generator',
          'Title Generation',
          'Social Media Sharing'
        ]
      },
      {
        id: 'frequency',
        type: 'select',
        question: 'How often do you use Kingdom Studio?',
        required: true,
        options: ['Daily', 'Weekly', 'Monthly', 'Rarely', 'First time user']
      },
      {
        id: 'description',
        type: 'textarea',
        question: 'Please describe your feedback in detail',
        required: true,
        placeholder: 'Tell us what\'s on your mind...',
        validation: { minLength: 10, maxLength: 1000 }
      },
      {
        id: 'contact_email',
        type: 'email',
        question: 'Email (optional - for follow-up)',
        placeholder: 'your@email.com'
      }
    ]
  },

  bug_report: {
    id: 'bug_report',
    title: 'Report a Bug',
    description: 'Help us fix issues by providing detailed information about the problem.',
    feedbackType: 'bug-report',
    submitText: 'Report Bug',
    questions: [
      {
        id: 'severity',
        type: 'radio',
        question: 'How severe is this bug?',
        required: true,
        options: ['Critical - App unusable', 'High - Major feature broken', 'Medium - Minor issue', 'Low - Cosmetic issue']
      },
      {
        id: 'feature_affected',
        type: 'select',
        question: 'Which feature is affected?',
        required: true,
        options: [
          'Video Captions',
          'Thumbnail Generation',
          'Audio Generation', 
          'Prompt Generator',
          'Script Generator',
          'Title Generation',
          'Login/Authentication',
          'File Upload',
          'Other'
        ]
      },
      {
        id: 'browser',
        type: 'text',
        question: 'Which browser are you using?',
        required: true,
        placeholder: 'e.g., Chrome 126, Safari 17, Firefox 120'
      },
      {
        id: 'steps_to_reproduce',
        type: 'textarea',
        question: 'Steps to reproduce the bug',
        required: true,
        placeholder: '1. Go to...\n2. Click on...\n3. Expected vs actual result',
        validation: { minLength: 20 }
      },
      {
        id: 'error_message',
        type: 'textarea',
        question: 'Any error messages? (if applicable)',
        placeholder: 'Copy and paste any error messages you see'
      },
      {
        id: 'contact_email',
        type: 'email',
        question: 'Email for follow-up',
        required: true,
        placeholder: 'your@email.com'
      }
    ]
  },

  feature_request: {
    id: 'feature_request', 
    title: 'Request a Feature',
    description: 'Suggest new features or improvements to make Kingdom Studio even better.',
    feedbackType: 'feature-request',
    submitText: 'Submit Request',
    questions: [
      {
        id: 'feature_category',
        type: 'select',
        question: 'Which area would this feature improve?',
        required: true,
        options: [
          'Video Editing',
          'Audio Processing',
          'AI Generation',
          'User Interface',
          'Collaboration',
          'Export/Sharing',
          'Performance',
          'Mobile Experience',
          'Other'
        ]
      },
      {
        id: 'priority',
        type: 'rating',
        question: 'How important is this feature to you?',
        required: true,
        validation: { min: 1, max: 5 }
      },
      {
        id: 'feature_description',
        type: 'textarea',
        question: 'Describe the feature you would like',
        required: true,
        placeholder: 'Describe what you want the feature to do...',
        validation: { minLength: 20, maxLength: 500 }
      },
      {
        id: 'use_case',
        type: 'textarea',
        question: 'How would you use this feature?',
        required: true,
        placeholder: 'Explain when and how you would use this feature...',
        validation: { minLength: 10, maxLength: 300 }
      },
      {
        id: 'willing_to_pay',
        type: 'radio',
        question: 'Would you be willing to pay extra for this feature?',
        options: ['Yes, definitely', 'Maybe, depends on price', 'No, should be free', 'Not sure']
      },
      {
        id: 'contact_email',
        type: 'email',
        question: 'Email (optional - for updates)',
        placeholder: 'your@email.com'
      }
    ]
  }
};

export const getFormById = (formId: string): FeedbackForm => {
  return feedbackForms[formId] || feedbackForms.general;
};

export const getAvailableForms = (): Array<{ id: string; title: string; description: string }> => {
  return Object.values(feedbackForms).map(form => ({
    id: form.id,
    title: form.title,
    description: form.description || ''
  }));
};
